'use client'

import Link from 'next/link'
import { usePathname, useRouter } from 'next/navigation'
import React, { createContext, useCallback, useContext, useEffect, useId, useRef, useState } from 'react'

import { asSkeleton, PREVIEW_SKELETON_COOKIE, SIDEBAR_COOKIE, type Skeleton } from './constants'
import { Icon, type IconName } from './Icon'
import { activeNavKey, switchLocalePath, type NavItem } from './nav'

const base = process.env.NEXT_PUBLIC_BASE_PATH ?? ''
const cookiePath = base || '/'

type NavState = {
  open: boolean
  setOpen: (open: boolean) => void
  collapsed: boolean
  setCollapsed: (collapsed: boolean) => void
  toggleRef: React.RefObject<HTMLButtonElement | null>
}
const NavContext = createContext<NavState | null>(null)
const useNav = () => {
  const ctx = useContext(NavContext)
  if (!ctx) throw new Error('NavProvider missing')
  return ctx
}

// Mobile drawer / studio sidebar state, reflected on <html> so plain CSS can lay the shell out.
export function NavProvider({ children, initialCollapsed = false }: { children: React.ReactNode; initialCollapsed?: boolean }) {
  const [open, setOpen] = useState(false)
  const [collapsed, setCollapsedState] = useState(initialCollapsed)
  const toggleRef = useRef<HTMLButtonElement | null>(null)
  const pathname = usePathname()

  useEffect(() => setOpen(false), [pathname])

  useEffect(() => {
    const html = document.documentElement
    if (open) html.setAttribute('data-nav-open', '')
    else html.removeAttribute('data-nav-open')
    if (!open) return
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      setOpen(false)
      toggleRef.current?.focus()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open])

  const setCollapsed = useCallback((value: boolean) => {
    setCollapsedState(value)
    document.documentElement.setAttribute('data-sidebar', value ? 'collapsed' : 'expanded')
    // Functional preference (no consent needed), read by the layout to avoid a flash on the next page.
    document.cookie = `${SIDEBAR_COOKIE}=${value ? 'collapsed' : 'expanded'}; path=${cookiePath}; max-age=31536000; SameSite=Lax`
  }, [])

  return (
    <NavContext.Provider value={{ open, setOpen, collapsed, setCollapsed, toggleRef }}>
      {children}
      {open && <div className="site-nav-backdrop" onClick={() => setOpen(false)} aria-hidden="true" />}
    </NavContext.Provider>
  )
}

const desktopQuery = '(min-width: 64rem)'

// Hamburger (drawer on small screens). In "sidebar" mode it collapses the sidebar on desktop instead.
export function MenuToggle({ labels, mode = 'drawer', controls = 'site-nav' }: { labels: { open: string; close: string; collapse?: string; expand?: string }; mode?: 'drawer' | 'sidebar'; controls?: string }) {
  const { open, setOpen, collapsed, setCollapsed, toggleRef } = useNav()
  const [desktop, setDesktop] = useState(false)
  useEffect(() => {
    if (mode !== 'sidebar') return
    const media = window.matchMedia(desktopQuery)
    const update = () => setDesktop(media.matches)
    update()
    media.addEventListener('change', update)
    return () => media.removeEventListener('change', update)
  }, [mode])

  const sidebarDesktop = mode === 'sidebar' && desktop
  const expanded = sidebarDesktop ? !collapsed : open
  const label = sidebarDesktop ? (collapsed ? labels.expand : labels.collapse) ?? labels.open : open ? labels.close : labels.open
  return (
    <button
      ref={toggleRef}
      type="button"
      className={`menu-toggle menu-toggle--${mode}`}
      aria-expanded={expanded}
      aria-controls={controls}
      aria-label={label}
      title={label}
      data-testid="menu-toggle"
      onClick={() => (sidebarDesktop ? setCollapsed(!collapsed) : setOpen(!open))}
    >
      <Icon name={sidebarDesktop ? 'sidebar' : open ? 'close' : 'menu'} size={22} />
    </button>
  )
}

export type NavEntry = NavItem & { label: string; icon: IconName }

export function NavPanel({ items, label, variant, children, id = 'site-nav' }: { items: NavEntry[]; label: string; variant: 'bar' | 'sidebar'; children?: React.ReactNode; id?: string }) {
  const pathname = usePathname()
  const { open } = useNav()
  const active = activeNavKey(items, pathname)
  const ref = useRef<HTMLElement>(null)

  useEffect(() => {
    if (!open) return
    // Next frame: the provider (parent effect, run after this one) must first flag <html> so the studio drawer is visible.
    const frame = requestAnimationFrame(() => ref.current?.querySelector<HTMLElement>('a, button')?.focus())
    return () => cancelAnimationFrame(frame)
  }, [open])

  return (
    <nav ref={ref} id={id} className={`site-nav site-nav--${variant}`} data-open={open ? 'true' : 'false'} aria-label={label} data-testid="site-nav">
      <ul className="site-nav__list">
        {items.map((item) => {
          const content = (
            <>
              <Icon name={item.icon} className="site-nav__icon" />
              <span className="site-nav__label">{item.label}</span>
            </>
          )
          return (
            <li key={item.key}>
              {item.external ? (
                <a className="site-nav__link" href={item.href} title={item.label} data-nav={item.key}>
                  {content}
                </a>
              ) : (
                <Link className="site-nav__link" href={item.href} title={item.label} data-nav={item.key} aria-current={active === item.key ? 'page' : undefined}>
                  {content}
                </Link>
              )}
            </li>
          )
        })}
      </ul>
      {children && <div className="site-nav__extra">{children}</div>}
    </nav>
  )
}

// Same page in the other language (path and query kept).
export function LanguageSwitcher({ target, label, short }: { target: 'en' | 'fr'; label: string; short?: boolean }) {
  const pathname = usePathname()
  const router = useRouter()
  const href = switchLocalePath(pathname, target)
  return (
    <Link
      href={href}
      hrefLang={target}
      lang={target}
      className="lang-switch"
      data-testid="language-switch"
      aria-label={label}
      onClick={(event) => {
        if (!window.location.search) return
        event.preventDefault()
        router.push(`${href}${window.location.search}`)
      }}
    >
      <Icon name="globe" size={18} />
      <span className="lang-switch__label">{short ? target.toUpperCase() : label}</span>
    </Link>
  )
}

type AccountLabels = { menu: string; profile: string; certificates: string; logout: string; dashboard: string; admin?: string }

export function AccountMenu({ name, roleLabel, locale, labels, adminHref }: { name: string; roleLabel: string; locale: string; labels: AccountLabels; adminHref?: string }) {
  const [open, setOpen] = useState(false)
  const [busy, setBusy] = useState(false)
  const router = useRouter()
  const pathname = usePathname()
  const ref = useRef<HTMLDivElement>(null)
  const panelId = useId()
  const initials = name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('')

  useEffect(() => setOpen(false), [pathname])
  useEffect(() => {
    if (!open) return
    // Closes on a tap or a focus outside (keyboard users tabbing away left it open over the page); Escape gives the
    // focus back to the button.
    const outside = (event: Event) => !ref.current?.contains(event.target as Node) && setOpen(false)
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      setOpen(false)
      if (ref.current?.contains(document.activeElement)) ref.current.querySelector<HTMLButtonElement>('.account-menu__button')?.focus()
    }
    document.addEventListener('pointerdown', outside)
    document.addEventListener('focusin', outside)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('pointerdown', outside)
      document.removeEventListener('focusin', outside)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  async function logout() {
    setBusy(true)
    await fetch(`${base}/api/users/logout`, { method: 'POST', credentials: 'include' }).catch(() => null)
    router.push(`/${locale}`)
    router.refresh()
  }

  return (
    <div className="account-menu" ref={ref}>
      <button type="button" className="account-menu__button" aria-expanded={open} aria-controls={panelId} aria-label={labels.menu} onClick={() => setOpen(!open)} data-testid="account-menu">
        <span className="avatar" aria-hidden="true">
          {initials || '?'}
        </span>
        <span className="account-menu__name">{name}</span>
        <Icon name="chevronDown" size={16} />
      </button>
      <div id={panelId} className="account-menu__panel" hidden={!open}>
        <p className="account-menu__who">
          <strong>{name}</strong>
          <span>{roleLabel}</span>
        </p>
        <Link href={`/${locale}/dashboard`}>
          <Icon name="dashboard" size={18} /> {labels.dashboard}
        </Link>
        <Link href={`/${locale}/profile`}>
          <Icon name="user" size={18} /> {labels.profile}
        </Link>
        <Link href={`/${locale}/certificates`}>
          <Icon name="certification" size={18} /> {labels.certificates}
        </Link>
        {adminHref && labels.admin && (
          <a href={adminHref}>
            <Icon name="admin" size={18} /> {labels.admin}
          </a>
        )}
        <button type="button" onClick={logout} disabled={busy} data-testid="logout">
          <Icon name="logout" size={18} /> {labels.logout}
        </button>
      </div>
    </div>
  )
}

function setPreviewCookie(name: string, value: string | null) {
  document.cookie = value ? `${name}=${value}; path=${cookiePath}; SameSite=Lax` : `${name}=; path=${cookiePath}; max-age=0; SameSite=Lax`
}

type PreviewLabels = {
  title: string
  skeleton: string
  reset: string
  note: string
  active: string
  skeletons: Record<Skeleton, string>
}

// Staff only: compare the skeletons without touching the site setting (session cookie); also applies ?skeleton=…
// (reset to go back) from the URL. The logo is the official one since 27/09: no logo preview any more.
export function DesignSwitcher({ skeleton, previewing, labels }: { skeleton: Skeleton; previewing: boolean; labels: PreviewLabels }) {
  const router = useRouter()
  const pathname = usePathname()
  const [open, setOpen] = useState(false)

  useEffect(() => {
    const value = new URLSearchParams(window.location.search).get('skeleton')
    let changed = false
    if (value === 'reset' || value === 'off') {
      setPreviewCookie(PREVIEW_SKELETON_COOKIE, null)
      changed = true
    } else if (asSkeleton(value) && value !== skeleton) {
      setPreviewCookie(PREVIEW_SKELETON_COOKIE, value)
      changed = true
    }
    if (changed) router.refresh()
    // Only on navigation: the refresh itself must not loop.
  }, [pathname])

  const choose = (cookie: string, value: string | null) => {
    setPreviewCookie(cookie, value)
    router.refresh()
  }

  return (
    <div className="design-switcher" data-open={open ? 'true' : 'false'}>
      <button type="button" className="design-switcher__toggle" aria-expanded={open} onClick={() => setOpen(!open)} data-testid="design-switcher">
        <Icon name="star" size={16} /> {labels.title}
        {previewing && <span className="badge">{labels.active}</span>}
      </button>
      {open && (
        <div className="design-switcher__panel">
          <fieldset>
            <legend>{labels.skeleton}</legend>
            {(Object.keys(labels.skeletons) as Skeleton[]).map((value) => (
              <button key={value} type="button" aria-pressed={value === skeleton} onClick={() => choose(PREVIEW_SKELETON_COOKIE, value)}>
                {labels.skeletons[value]}
              </button>
            ))}
          </fieldset>
          <button
            type="button"
            className="design-switcher__reset"
            onClick={() => choose(PREVIEW_SKELETON_COOKIE, null)}
          >
            {labels.reset}
          </button>
          <p>{labels.note}</p>
        </div>
      )}
    </div>
  )
}
